import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useGetWaitListStatus } from '~entities/studios/api/get-wait-list-status';
import { useUpdateWaitListStatus } from '~entities/studios/api/update-wait-list-status';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { Switch } from '~shared/shadcn/ui/switch';

const DEFAULT_REMINDERS = {
  daysBefore: 2 as 1 | 2 | 3,
  recipients: 'parent' as 'parent' | 'teacher' | 'both',
  privateLessonsEnabled: false,
};

const DEFAULT_MEMBERSHIP_FEE = {
  enabled: false,
  returningAmount: 0,
  newAmount: 0,
};

export function RemindersSettings() {
  const { data: featureSettings, isLoading } = useGetWaitListStatus();
  const updateFeatureSettings = useUpdateWaitListStatus();
  const [daysBefore, setDaysBefore] = useState<1 | 2 | 3>(
    DEFAULT_REMINDERS.daysBefore,
  );
  const [recipients, setRecipients] = useState<'parent' | 'teacher' | 'both'>(
    DEFAULT_REMINDERS.recipients,
  );
  const [privateLessonsEnabled, setPrivateLessonsEnabled] = useState(false);
  const [membershipFeesEnabled, setMembershipFeesEnabled] = useState(false);
  const [returningAmount, setReturningAmount] = useState('0');
  const [newAmount, setNewAmount] = useState('0');

  useEffect(() => {
    if (!featureSettings?.lessonReminders) return;
    const reminders = featureSettings.lessonReminders;
    if (
      reminders.daysBefore === 1 ||
      reminders.daysBefore === 2 ||
      reminders.daysBefore === 3
    ) {
      setDaysBefore(reminders.daysBefore);
    }
    if (
      reminders.recipients === 'parent' ||
      reminders.recipients === 'teacher' ||
      reminders.recipients === 'both'
    ) {
      setRecipients(reminders.recipients);
    }
    setPrivateLessonsEnabled(Boolean(reminders.privateLessonsEnabled));
  }, [featureSettings]);

  useEffect(() => {
    const membership = featureSettings?.membershipFeeReminders;
    if (!membership) {
      setMembershipFeesEnabled(DEFAULT_MEMBERSHIP_FEE.enabled);
      setReturningAmount(String(DEFAULT_MEMBERSHIP_FEE.returningAmount));
      setNewAmount(String(DEFAULT_MEMBERSHIP_FEE.newAmount));
      return;
    }
    setMembershipFeesEnabled(Boolean(membership.enabled));
    setReturningAmount(String(membership.returningAmount ?? 0));
    setNewAmount(String(membership.newAmount ?? 0));
  }, [featureSettings]);

  const handleSave = () => {
    const parsedReturning = Number(returningAmount);
    const parsedNew = Number(newAmount);
    if (
      !Number.isFinite(parsedReturning) ||
      parsedReturning < 0 ||
      !Number.isFinite(parsedNew) ||
      parsedNew < 0
    ) {
      toast.error('Membership fee amounts must be valid numbers 0 or greater.');
      return;
    }

    updateFeatureSettings.mutate(
      {
        lessonReminders: {
          daysBefore,
          recipients,
          privateLessonsEnabled,
        },
        membershipFeeReminders: {
          enabled: membershipFeesEnabled,
          returningAmount: parsedReturning,
          newAmount: parsedNew,
        },
      },
      {
        onSuccess: () => {
          toast.success('Reminder settings have been updated.');
        },
        onError: () => {
          toast.error('Failed to update reminder settings. Please try again.');
        },
      },
    );
  };

  return (
    <div className='p-4'>
      <Link to='/dashboard' className='mb-8 flex items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <span className='text-lg font-semibold'>Back to Dashboard</span>
      </Link>

      <Card className='mx-auto max-w-4xl'>
        <CardHeader>
          <CardTitle>Reminders</CardTitle>
          <CardDescription>
            Configure when reminders are scheduled and who receives them. To
            enable or customize the email template itself, use{' '}
            <Link
              to='/email-settings'
              className='font-medium text-foreground underline underline-offset-2'
            >
              Email Settings
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='space-y-2'>
              <Label htmlFor='reminder-days'>Send reminder</Label>
              <Select
                value={String(daysBefore)}
                onValueChange={(value) => {
                  setDaysBefore(Number(value) as 1 | 2 | 3);
                }}
                disabled={isLoading}
              >
                <SelectTrigger id='reminder-days'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='1'>1 day before</SelectItem>
                  <SelectItem value='2'>2 days before</SelectItem>
                  <SelectItem value='3'>3 days before</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-2'>
              <Label htmlFor='reminder-recipients'>Send to</Label>
              <Select
                value={recipients}
                onValueChange={(value) => {
                  setRecipients(value as 'parent' | 'teacher' | 'both');
                }}
                disabled={isLoading}
              >
                <SelectTrigger id='reminder-recipients'>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value='parent'>Parent / student</SelectItem>
                  <SelectItem value='teacher'>Teacher / instructor</SelectItem>
                  <SelectItem value='both'>Parent and teacher</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <ReminderTypeToggle
            label='Schedule private lesson reminders'
            description='Turn automated scheduling on for booked private lessons using the timing and recipients above. The email template is controlled in Email Settings.'
            checked={privateLessonsEnabled}
            onCheckedChange={setPrivateLessonsEnabled}
            disabled={isLoading}
          />

          <div className='space-y-4 rounded-lg border bg-card p-5'>
            <ReminderTypeToggle
              label='Weekly fees owed reminder'
              description='Email studio staff every Monday with outstanding fees (class, registration, membership, etc.). The first 50 rows are in the email; the rest attach as CSV. Optional membership amounts below also include never-invoiced membership families. Off by default. The email template is controlled in Email Settings.'
              checked={membershipFeesEnabled}
              onCheckedChange={setMembershipFeesEnabled}
              disabled={isLoading}
              bare
            />
            <div className='grid gap-4 sm:grid-cols-2'>
              <div className='space-y-2'>
                <Label htmlFor='returning-membership-amount'>
                  Returning family amount
                </Label>
                <Input
                  id='returning-membership-amount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={returningAmount}
                  onChange={(event) => setReturningAmount(event.target.value)}
                  disabled={isLoading || !membershipFeesEnabled}
                />
              </div>
              <div className='space-y-2'>
                <Label htmlFor='new-membership-amount'>New family amount</Label>
                <Input
                  id='new-membership-amount'
                  type='number'
                  min='0'
                  step='0.01'
                  value={newAmount}
                  onChange={(event) => setNewAmount(event.target.value)}
                  disabled={isLoading || !membershipFeesEnabled}
                />
              </div>
            </div>
          </div>

          <div className='flex justify-end pt-4'>
            <Button
              onClick={handleSave}
              disabled={isLoading || updateFeatureSettings.isPending}
            >
              {updateFeatureSettings.isPending ? (
                <>
                  <Loader2 className='mr-2 size-4 animate-spin' />
                  Saving Reminder Settings
                </>
              ) : (
                'Save Reminder Settings'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ReminderTypeToggle({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  bare = false,
}: {
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  bare?: boolean;
}) {
  const content = (
    <div className='flex items-center justify-between gap-4'>
      <div className='space-y-1'>
        <label className='text-sm font-medium'>{label}</label>
        <p className='text-xs text-muted-foreground'>{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        aria-label={label}
      />
    </div>
  );

  if (bare) return content;
  return <div className='rounded-lg border bg-card p-5'>{content}</div>;
}
